<template>
  <b-card-code>
    <validation-observer ref="simpleRules">
      <h4 class="mb-3" v-if="(pending || rejected) && specialRemarks"><span class="text-danger">NOTE : </span> {{specialRemarks}}</h4>
      <b-form @submit.prevent="submitForm">
        <div v-if="showAgreementSection" class="agreement-section mb-3 pb-3 border-bottom">
          <h5 class="mb-2">Digital Agreement</h5>
          <p class="mb-2 text-muted">
            Please review the agreement below, then sign to complete your registration.
          </p>

          <agreement-pdf-scroller
            v-if="agreementPdfUrl"
            :pdf-url="agreementPdfUrl"
            class="mb-2"
          />
          <div v-if="agreementPdfUrl" class="mb-3">
            <a :href="agreementPdfUrl" target="_blank" rel="noopener">Open PDF in new tab</a>
          </div>
          <b-alert v-else show variant="warning" class="mb-3 py-2">
            Agreement PDF is not available right now.
          </b-alert>

          <label class="font-weight-bold d-block mb-1">Digital Signature</label>
          <div v-if="hasSavedSignature" class="signed-signature-block">
            <b-alert show variant="success" class="mb-2 py-2">
              Your digital agreement has been signed.
            </b-alert>
            <attachment :data="agreementSignature" />
          </div>
          <div v-else-if="capturedSignature" class="signed-signature-block">
            <b-alert show variant="info" class="mb-2 py-2">
              Signature captured. Click <strong>Save changes</strong> below to submit your KYC.
            </b-alert>
            <b-img :src="capturedSignature" class="captured-signature-img" />
            <b-button size="sm" variant="outline-primary" class="mt-2" @click="openSignModal">
              Sign Again
            </b-button>
          </div>
          <div v-else-if="showSignaturePad" class="agreement-actions">
            <b-button variant="primary" @click="openSignModal">Sign Agreement</b-button>
          </div>

          <b-modal
            v-model="showSignModal"
            title="Sign Agreement"
            size="lg"
            hide-footer
            @shown="onSignModalShown"
          >
            <p class="text-muted mb-2">Draw your signature below to confirm this agreement.</p>
            <digital-agreement-signature-pad ref="agreementSignaturePad" />
            <b-form-checkbox v-model="localAgreementConfirmed" class="mt-2">
              I have read and agree to the terms stated in this agreement.
            </b-form-checkbox>
            <div class="d-flex flex-wrap mt-3">
              <b-button variant="outline-secondary" class="mr-1" @click="showSignModal = false">
                Close
              </b-button>
              <b-button variant="primary" @click="confirmModalSignature">
                Submit Signature
              </b-button>
            </div>
          </b-modal>
        </div>
        <b-row>
          <b-col md="6">
            <b-form-group>
              <label>Pan No</label>
              <validation-provider #default="{ errors }" rules="required" name="Pan No">
                <b-form-input v-model="panno" maxlength="10" @input="validatePanClick($event)"
                  placeholder="Enter PAN No" />
                <small v-if="pannoValidate == false" class="text-danger">Enter Valid PAN No</small>

                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group>
              <label>Aadhar No</label>
              <validation-provider #default="{ errors }" rules="required" name="Aadhar no">
                <b-form-input v-model="aadharcardno" maxlength="14" @input="validateAadharClick($event)"
                  placeholder="Enter Aadhar No" />
                <small v-if="adharnoValidate == false" class="text-danger">Enter Valid Aadhar No</small>
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>

          <b-col md="6">
            <b-form-group>
              <label>Bank</label>
              <b-form-input v-model="bankname" :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                placeholder="Enter Bank" rules="required" />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group>
              <label>Branch</label>
              <b-form-input v-model="bankbranch" :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                placeholder="Enter Branch" rules="required" />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group>
              <label>Account No</label>
              <b-form-input v-model="bankaccno" type="number" :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                placeholder="Enter Account No" />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group>
              <label>IFSC Code</label>
              <b-form-input v-model="ifsccode" maxlength="11" @input="validateIFSCCodeClick($event)"
                placeholder="Enter IFSC Code" />
              <small v-if="ifsccodeValidate == false" class="text-danger">Enter Valid IFSC Code</small>
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group>
              <label>Pan Card</label>
              <validation-provider #default="{ errors }" rules="required" name="Pancard">
                <b-form-file v-model="pancard" placeholder="Select Pan Card" drop-placeholder="Drop file here..."
                  accept="image/*" @input="handleFile($event, 'pancard')" />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
            <attachment :data="pancard" />
          </b-col>
          <b-col md="6">
            <b-form-group>
              <validation-provider #default="{ errors }" rules="required" name="Aadhar Front side">
                <label>Aadhar Card Front Side</label>
                <b-form-file v-model="aadharfrontside" placeholder="Select Aadhar Card Front Side"
                  drop-placeholder="Drop file here..." accept="image/*" @input="handleFile($event, 'aadharfrontside')" />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
            <attachment :data="aadharfrontside" />
          </b-col>
          <b-col md="6">
            <b-form-group>
              <validation-provider #default="{ errors }" rules="required" name="Aadhar Back side">
                <label>Aadhar Card Back Side</label>
                <b-form-file v-model="aadharbackside" placeholder="Select Aadhar Card Back Side"
                  drop-placeholder="Drop file here..." accept="image/*" @input="handleFile($event, 'aadharbackside')" />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>

            <attachment :data="aadharbackside" />
          </b-col>
          <!-- <b-col md="6" v-if="showCompanyAssociatePartnerAgreement">
            <b-form-group>
              <validation-provider #default="{ errors }" rules="required" name="Company Associate Partner Joining Agreement">
                <label>Company Associate Partner Joining Agreement</label>
                <b-form-file v-model="companyassociatepartnerjoiningagreement" placeholder="Select Company Associate Partner Joining Agreement"
                  drop-placeholder="Drop file here..." accept="image/*,.pdf"
                  @input="handleFile($event, 'companyassociatepartnerjoiningagreement')" />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
            <attachment :data="companyassociatepartnerjoiningagreement" />
          </b-col>
          <b-col md="6" v-if="showBusinessPartnerAgreement">
            <b-form-group>
              <validation-provider #default="{ errors }" rules="required" name="Business Partner Joining Agreement">
                <label>Business Partner Joining Agreement</label>
                <b-form-file v-model="businesspartnerjoiningagreement" placeholder="Select Business Partner Joining Agreement"
                  drop-placeholder="Drop file here..." accept="image/*,.pdf"
                  @input="handleFile($event, 'businesspartnerjoiningagreement')" />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
            <attachment :data="businesspartnerjoiningagreement" />
          </b-col>
          <b-col md="6" v-if="showAssociatePartnerAgreement">
            <b-form-group>
              <validation-provider #default="{ errors }" rules="required" name="Associate Partner Joining Agreement">
                <label>Associate Partner Joining Agreement</label>
                <b-form-file v-model="associatepartnerjoiningagreement" placeholder="Select Associate Partner Joining Agreement"
                  drop-placeholder="Drop file here..." accept="image/*,.pdf"
                  @input="handleFile($event, 'associatepartnerjoiningagreement')" />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
            <attachment :data="associatepartnerjoiningagreement" />
          </b-col> -->
          <b-col cols="12">
            <b-button variant="primary" class="mt-2 mr-1" type="submit" :disabled="!flag" @click.prevent="submitForm">
              Save changes
            </b-button>
            <b-button v-if="!back" variant="outline-secondary" type="reset" class="mt-2" @click="handleBack()">
              Back
            </b-button>
            <b-button v-if="back" variant="danger" class="mt-2" @click="handleLogout()">
              Logout
            </b-button>
          </b-col>
        </b-row>
      </b-form>
    </validation-observer>
  </b-card-code>
</template>

<script>
import { ValidationProvider, ValidationObserver } from "vee-validate";
import useAppConfig from "@core/app-config/useAppConfig";
import {
  BFormInput,
  BFormGroup,
  BForm,
  BRow,
  BCol,
  BButton,
  BFormTextarea,
  BFormDatepicker,
  BImgLazy,
  BFormFile,
  BFormCheckbox,
  BAlert,
  BImg,
  BModal,
} from "bootstrap-vue";
import AgreementPdfScroller from "@/components/AgreementPdfScroller.vue";
import DigitalAgreementSignaturePad from "@/components/DigitalAgreementSignaturePad.vue";
import {
  required,
  email,
  confirmed,
  url,
  between,
  alpha,
  integer,
  password,
  min,
  digits,
  alphaDash,
  length,
  min_value,
} from "@validations";
import BCardCode from "@core/components/b-card-code";
import Attachment from "../../../components/Attechment.vue";
import axios from '@/components/axios';
import useJwt from "@/auth/jwt/useJwt";
import { initialAbility } from "@/libs/acl/config";

export default {
  components: {
    BCardCode,
    ValidationProvider,
    ValidationObserver,
    BFormInput,
    BFormGroup,
    Attachment,
    BForm,
    BRow,
    BCol,
    BButton,
    BFormTextarea,
    BFormDatepicker,
    BImgLazy,
    BFormFile,
    BFormCheckbox,
    BAlert,
    BImg,
    BModal,
    DigitalAgreementSignaturePad,
    AgreementPdfScroller,
  },
  props: {
    aboutData: {
      type: Object,
      default: () => { },
    },
    back: {
      default: true,
    },
    pending: {
      default: false,
    },
    rejected: {
      default: false,
    },
    useractivityProp: {
      type: String,
      default: "",
    },
    requireDigitalSignature: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      agreementSignature: "",
      partnerUuid: "",
      capturedSignature: "",
      showSignModal: false,
      localAgreementConfirmed: false,
      aadharcardno: "",
      panno: "",
      aadharfrontside: "",
      aadharbackside: "",
      pancard: "",
      companyassociatepartnerjoiningagreement: "",
      businesspartnerjoiningagreement: "",
      associatepartnerjoiningagreement: "",
      useractivity: "",
      pannoValidate: true,
      adharnoValidate: true,
      flag: true,
      userData: JSON.parse(localStorage.getItem("userData")),
      baseApi: process.env.VUE_APP_APIENDPOINT,
      accessToken: localStorage.getItem("accessToken"),
      bankaccno: '',
      bankname: '',
      bankbranch: '',
      ifsccode: '',
      ifsccodeValidate:true,
      specialRemarks:''
    };
  },
  computed: {
    normalizedUserActivity() {
      const activity = (this.useractivity || this.useractivityProp || "").trim().toLowerCase();
      if (activity === "business partner") {
        return "business partner";
      }
      if (activity === "associate partner") {
        return "associate partner";
      }
      return "company associate partner";
    },
    showCompanyAssociatePartnerAgreement() {
      return this.normalizedUserActivity === "company associate partner";
    },
    showBusinessPartnerAgreement() {
      return this.normalizedUserActivity === "business partner";
    },
    showAssociatePartnerAgreement() {
      return this.normalizedUserActivity === "associate partner";
    },
    isCompanyAssociatePartner() {
      return (this.userData?.role || "").toLowerCase() === "companyassociatepartner";
    },
    hasAgreementSignature() {
      return this.hasSavedSignature || !!(this.capturedSignature && String(this.capturedSignature).trim());
    },
    hasSavedSignature() {
      return !!(this.agreementSignature && String(this.agreementSignature).trim());
    },
    showAgreementSection() {
      if (!this.isCompanyAssociatePartner) {
        return false;
      }
      return this.requireDigitalSignature || this.hasSavedSignature;
    },
    showSignaturePad() {
      return this.requireDigitalSignature && !this.hasSavedSignature;
    },
    agreementApiBase() {
      return (process.env.VUE_APP_CP_REGISTRATION_API_URL || "https://api.erp.nestoriagroup.com").replace(/\/$/, "");
    },
    agreementPdfUrl() {
      if (!this.partnerUuid) {
        return "";
      }
      return `${this.agreementApiBase}/digital-agreement/${this.partnerUuid}/pdf`;
    },
  },
  watch: {
    useractivityProp(value) {
      if (value) {
        this.useractivity = value;
      }
    },
  },
  mounted() {
    if (this.useractivityProp) {
      this.useractivity = this.useractivityProp;
    }
    this.getUserData();
  },
  methods: {
    validateIFSCCodeClick(e) {
      const regex = new RegExp(/^[A-Z]{4}0[A-Z0-9]{6}$/);
      this.ifsccode = this.ifsccode.toUpperCase();
      if (regex.test(this.ifsccode) == true) {
        this.ifsccodeValidate = true;
      } else {
        this.ifsccodeValidate = false;
      }
    },
    async getUserData() {
      await axios({
        method: "GET",
        url: `${this.baseApi}/getusersbyid/${this.userData.id}`,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.accessToken}`,
        },
      }).then((response) => {
        this.getEditValue(response.data.data[0]);
      });
    },
    getEditValue(item) {
      this.useractivity = item.useractivity || this.useractivityProp || "";
      this.aadharcardno = item.aadharcardno;
      this.panno = item.panno;
      this.aadharfrontside = item.aadharfrontside;
      this.aadharbackside = item.aadharbackside;
      this.pancard = item.pancard;
      this.companyassociatepartnerjoiningagreement =
        item.companyassociatepartnerjoiningagreement || item.joiningagreement;
      this.businesspartnerjoiningagreement = item.businesspartnerjoiningagreement || "";
      this.associatepartnerjoiningagreement = item.associatepartnerjoiningagreement || "";
      this.bankaccno= item.bankaccno;
      this.bankbranch= item.bankbranch;
      this.bankname= item.bankname;
      this.ifsccode= item.ifsccode;
      this.specialRemarks = item.specialremarks
      this.agreementSignature = item.agreement_signature || "";
      this.partnerUuid = item.uuid || "";

    },
    openSignModal() {
      this.localAgreementConfirmed = false;
      this.showSignModal = true;
    },
    onSignModalShown() {
      this.$nextTick(() => {
        const pad = this.$refs.agreementSignaturePad;
        if (pad && typeof pad.initCanvas === "function") {
          pad.initCanvas();
        }
      });
    },
    confirmModalSignature() {
      if (!this.localAgreementConfirmed) {
        this.$swal({
          title: "Warning",
          timer: 5000,
          text: "Please confirm that you agree to the agreement terms.",
          icon: "warning",
          customClass: { confirmButton: "btn btn-primary" },
          buttonsStyling: false,
        });
        return;
      }

      const pad = this.$refs.agreementSignaturePad;
      const signature = pad ? pad.getSignatureDataUrl() : null;
      if (!signature) {
        this.$swal({
          title: "Warning",
          timer: 5000,
          text: "Please draw your digital signature before submitting.",
          icon: "warning",
          customClass: { confirmButton: "btn btn-primary" },
          buttonsStyling: false,
        });
        return;
      }

      this.capturedSignature = signature;
      this.showSignModal = false;
    },
    validatePanClick(e) {
      const regex = new RegExp(/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/);
      this.panno = this.panno.toUpperCase();
      if (regex.test(this.panno) == true) {
        this.pannoValidate = true;
      } else {
        this.pannoValidate = false;
      }
    },
    validateAadharClick(e) {
      const regex = new RegExp(/^[2-9]{1}[0-9]{3}\s{1}[0-9]{4}\s{1}[0-9]{4}$/);
      this.aadharcardno.length == 4
        ? (this.aadharcardno = this.aadharcardno.concat(" "))
        : this.aadharcardno;
      this.aadharcardno.length == 9
        ? (this.aadharcardno = this.aadharcardno.concat(" "))
        : this.aadharcardno;
      if (regex.test(this.aadharcardno) == true) {
        this.adharnoValidate = true;
      } else {
        this.adharnoValidate = false;
      }
    },
    async handleFile(e, type) {
      this.flag = false;
      const accessToken = localStorage.getItem("accessToken");
      const baseApi = process.env.VUE_APP_APIENDPOINT;
      const formData = new FormData();
      formData.append("image", e);
      formData.append("type", type);

      const requestoption = {
        method: "POST",
        headers: {
          "Contant-type": "multipart/form-data",
          Authorization: `Bearer ${accessToken}`,
        },
        data: formData,
        url: `${baseApi}/attachment`,
      };
      await axios(requestoption)
        .then((response) => {
          if (type == "aadharfrontside") {
            this.aadharfrontside = process.env.VUE_APP_IMAGE_PATH + response.data.attachment;
          } else if (type == "aadharbackside") {
            this.aadharbackside = process.env.VUE_APP_IMAGE_PATH + response.data.attachment;
          } else if (type == "companyassociatepartnerjoiningagreement") {
            this.companyassociatepartnerjoiningagreement = process.env.VUE_APP_IMAGE_PATH + response.data.attachment;
          } else if (type == "businesspartnerjoiningagreement") {
            this.businesspartnerjoiningagreement = process.env.VUE_APP_IMAGE_PATH + response.data.attachment;
          } else if (type == "associatepartnerjoiningagreement") {
            this.associatepartnerjoiningagreement = process.env.VUE_APP_IMAGE_PATH + response.data.attachment;
          } else {
            this.pancard = process.env.VUE_APP_IMAGE_PATH + response.data.attachment;
          }
        })
        .catch((error) => console.log(error, "error"))
        .finally(() => {
          this.flag = true;
        })
    },
    submitForm(e) {
      this.flag = false
      const data = {
        aadharcardno: this.aadharcardno,
        panno: this.panno,
        aadharbackside: this.aadharbackside,
        aadharfrontside: this.aadharfrontside,
        pancard: this.pancard,
        companyassociatepartnerjoiningagreement: this.companyassociatepartnerjoiningagreement,
        businesspartnerjoiningagreement: this.businesspartnerjoiningagreement,
        associatepartnerjoiningagreement: this.associatepartnerjoiningagreement,
        joiningagreement: this.showCompanyAssociatePartnerAgreement
          ? this.companyassociatepartnerjoiningagreement
          : this.showBusinessPartnerAgreement
            ? this.businesspartnerjoiningagreement
            : this.associatepartnerjoiningagreement,

        bankaccno: this.bankaccno,
        bankbranch: this.bankbranch,
        bankname: this.bankname,
        ifsccode: this.ifsccode,

      };

      // var ifEdit = this.$route.query.id ? true : false;
      this.$refs.simpleRules.validate().then(async (success) => {
        if (!success || !this.adharnoValidate || !this.pannoValidate || !this.ifsccodeValidate) {
          this.$swal({
            title: "Warning",
            timer: 5000,
            text: "Plese Fill the All Field",
            icon: "warning",
            customClass: {
              confirmButton: "btn btn-primary",
            },
            buttonsStyling: false,
          });
          this.flag = true

        }
        if (success && this.adharnoValidate && this.pannoValidate && this.ifsccodeValidate ) {
          if (this.showSignaturePad) {
          const signature = this.capturedSignature
              || (this.$refs.agreementSignaturePad ? this.$refs.agreementSignaturePad.getSignatureDataUrl() : null);
            if (!signature) {
              this.$swal({
                title: "Warning",
                timer: 5000,
                text: "Please click Sign Agreement and submit your signature before saving.",
                icon: "warning",
                customClass: { confirmButton: "btn btn-primary" },
                buttonsStyling: false,
              });
              this.flag = true;
              return;
            }
            data.approved = true;
            data.agreement_signature = signature;
          }

          const accessToken = localStorage.getItem("accessToken");
          const baseApi = process.env.VUE_APP_APIENDPOINT;
          await axios({
            method: "PUT",
            url: `${baseApi}/auth/userupdate/${this.userData.id}`,
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${accessToken}`,
            },
            data,
          })
            .then((json) => {
              this.$store.dispatch('app/getUserData')
              this.$swal({
                title: "Submited",
                text: json.data.message
                  ? `${json.data.message}`
                  : json.data.success
                    ? `${json.data.success}`
                    : "Update SuccessFully",
                icon: "success",
                customClass: {
                  confirmButton: "btn btn-primary",
                },
                buttonsStyling: false,
              });
              this.$forceUpdate();
              this.$emit("saved");
            })
            .catch((error) => {
              this.$swal({
                title: "Error!",
                text: `${error}`,
                icon: "error",
                timer: 5000,
                customClass: {
                  confirmButton: "btn btn-primary",
                },
                buttonsStyling: false,
              });
            })
            .finally(() => {
              this.flag = true

            })
        }
      });
    },
    handleBack() {
      this.$router.push("/");
    },
    async handleLogout() {
      const requestOptionsUsers = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.accessToken}`,
        },
        url: `${this.baseApi}/logout`,
      };

      await axios(requestOptionsUsers).then((response) => {
        // Get user role before clearing localStorage
        const userData = JSON.parse(localStorage.getItem("userData") || "{}");
        const isAdmin = userData && (userData.role === "admin" || userData.role === "Admin");

        // Remove userData from localStorage
        // ? You just removed token from localStorage. If you like, you can also make API call to backend to blacklist used token
        localStorage.removeItem(useJwt.jwtConfig.storageTokenKeyName);
        localStorage.removeItem(useJwt.jwtConfig.storageRefreshTokenKeyName);

        // Remove userData from localStorage

        const keys = Object.keys(localStorage);
        let i = keys.length;

        while (i--) {
          localStorage.removeItem(`${keys[i]}`);
        }
        // Reset ability
        this.$ability.update(initialAbility);

        // Redirect based on user role
        if (isAdmin) {
          // Admin users should stay on /login/admin after logout
          this.$router.replace({ name: "auth-admin-login" });
        } else {
          // Non-admin users should stay on /login after logout
          this.$router.replace({ name: "auth-login" });
        }
      });
    },
  },
};
</script>

<style scoped>
.agreement-section {
  background: #f8fafc;
  border-radius: 8px;
  padding: 1rem;
}

.captured-signature-img {
  max-width: 320px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: #fff;
  padding: 8px;
}

.agreement-actions {
  margin-top: 0.5rem;
}
</style>
